import { FC, Fragment, useCallback } from "react";
import {
  Collapse,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useToggle } from "rooks";
import { IconByResourceType } from "../../../../../../common/IconByResourceType";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";
import { ResourceGroupType } from "@/utils/constants/resources/resourceGroupsByProvider";
import { ResourceType } from "@/services/cloudchipr.api";
import { useResourceVisibilityHook } from "@/components/pages/automations/common/utils/hooks/useResourceVisibility.hook";
import { useAppSelector } from "@/store/hooks";
import { automationProviderSelector } from "@/store/automations/selectros/common/fields/automationProviderSelector";
import { automationAccountIdsSelector } from "@/store/automations/selectros/common/fields/automationAccountIdsSelector";
import { automationTypeSelector } from "@/store/automations/selectros/common/automationTypeSelector";

const collapseTransitionDuration = 200;

interface AutomationResourceSelectCollapsableItemProps {
  selectedValue?: ResourceType;
  resourcesGroup: ResourceGroupType;
  allNestedResourcesSelected: boolean;
  onChange(rt: ResourceType): void;
  recalculateMenuPosition(): void;
  isResourceTypeDisabled(rt: string): boolean;
}

export const AutomationResourceSelectCollapsableItem: FC<
  AutomationResourceSelectCollapsableItemProps
> = ({
  resourcesGroup,
  selectedValue,
  isResourceTypeDisabled,
  onChange,
  recalculateMenuPosition,
  allNestedResourcesSelected,
}) => {
  const automationType = useAppSelector(automationTypeSelector);
  const provider = useAppSelector(automationProviderSelector);
  const accountIds = useAppSelector(automationAccountIdsSelector);

  const isResourceTypeVisible = useResourceVisibilityHook({
    provider,
    visibleFor: automationType,
    accountIds: accountIds.join(", "),
  });

  const selected =
    !!selectedValue &&
    resourcesGroup.actionableResourceTypes.includes(selectedValue);

  const [collapsed, setCollapsed] = useToggle(selected);

  const collapseHandler = useCallback(
    (e: any) => {
      if (allNestedResourcesSelected) {
        return;
      }

      setCollapsed();
      e.stopPropagation();
      setTimeout(recalculateMenuPosition, collapseTransitionDuration);
    },
    [allNestedResourcesSelected, setCollapsed, recalculateMenuPosition],
  );

  return (
    <Fragment>
      {[
        <MenuItem
          onClickCapture={collapseHandler}
          disabled={allNestedResourcesSelected}
          key={`${resourcesGroup.value}_collapse-button`}
        >
          <ListItemIcon>
            <IconByResourceType
              resourceType={resourcesGroup.value}
              primary={selected}
            />
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography variant="body1" color="text.primary">
                {getResourceTypeName(resourcesGroup.value, {
                  service: true,
                  type: "long",
                })}
              </Typography>
            }
          />

          {collapsed ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>,

        <Collapse
          in={collapsed}
          unmountOnExit
          timeout={collapseTransitionDuration}
          key={`${resourcesGroup.value}_collapse`}
        >
          {resourcesGroup.actionableResourceTypes.map((rt) => {
            if (!isResourceTypeVisible(rt)) {
              return null;
            }

            return (
              <MenuItem
                key={`${rt}_collapse-item`}
                className={rt === selectedValue ? "Mui-selected" : ""}
                sx={{ pl: 6.25 }}
                disabled={isResourceTypeDisabled(rt) && rt !== selectedValue}
                onClick={onChange.bind(null, rt)}
              >
                {getResourceTypeName(rt, { type: "long" })}
              </MenuItem>
            );
          })}
        </Collapse>,
      ]}
    </Fragment>
  );
};
